import { THEME_ENGINE_VERSION, type ThemePackage, type SettingField, type Localized } from "./format";
import { REFERENCE_THEME, REFERENCE_THEME_ID } from "./reference-theme";

/**
 * Built-in theme catalog for the Secure Theme Engine.
 *
 * The marketplace needs a believable spread of first-party themes. Each theme
 * here is a fully valid, sandboxed `ThemePackage` (manifest + bilingual
 * settings + tokens + layout built only from vetted platform components +
 * scoped CSS). Built-in themes are always resolvable from code, so storefront
 * rendering never depends on the integrity of a DB-stored package.
 *
 * `makeBuiltinTheme` keeps the packages DRY: every theme shares the same
 * vetted layout/template structure as the reference theme and only varies its
 * palette, typography, corner style, and bilingual copy. This guarantees the
 * layout tree only ever references KNOWN_COMPONENTS.
 */

export type ThemeCategory =
  | "general"
  | "fashion"
  | "electronics"
  | "beauty"
  | "food"
  | "luxury"
  | "minimal";

/**
 * Marketplace presentation metadata for a built-in theme. This is catalog/
 * storefront-listing data (rating, install count, price) — intentionally
 * separate from the engine `ThemePackage` contract.
 */
export interface ThemeMarketplaceMeta {
  slug: string;
  category: ThemeCategory;
  /** Average review rating, 0–5. */
  rating: number;
  /** Number of stores using the theme (display only). */
  installs: number;
  /** Price in the smallest currency unit (halalas). 0 = Free. */
  priceCents: number;
  /** Optional "was" price for on-sale themes (halalas). */
  compareAtCents?: number;
  /** ISO date the theme was published — drives "newest" sort. */
  publishedAt: string;
  /** Up to ~5 colors that represent the theme, for swatch chips. */
  swatches: string[];
  /** Bilingual feature bullet points shown on the detail page. */
  features: Array<{ ar: string; en: string }>;
}

interface ThemeConfig {
  id: string;
  name: Localized;
  description: Localized;
  version?: string;
  author?: string;
  palette: {
    primary: string;
    accent: string;
    background: string;
    text: string;
    headerBg: string;
    footerBg: string;
    footerText: string;
  };
  fontAr?: string;
  fontEn?: string;
  /** Default corner radius token value (matches a cornerStyle option). */
  corner?: "0px" | "8px" | "16px" | "999px";
  announcement: Localized;
  showPromoStrip?: boolean;
  showNewsletter?: boolean;
}

function buildSettings(cfg: ThemeConfig): SettingField[] {
  return [
    {
      key: "primaryColor",
      type: "color",
      label: { ar: "اللون الأساسي", en: "Primary color" },
      group: { ar: "الألوان", en: "Colors" },
      default: cfg.palette.primary,
    },
    {
      key: "accentColor",
      type: "color",
      label: { ar: "لون التمييز", en: "Accent color" },
      group: { ar: "الألوان", en: "Colors" },
      default: cfg.palette.accent,
    },
    {
      key: "backgroundColor",
      type: "color",
      label: { ar: "لون الخلفية", en: "Background color" },
      group: { ar: "الألوان", en: "Colors" },
      default: cfg.palette.background,
    },
    {
      key: "textColor",
      type: "color",
      label: { ar: "لون النص", en: "Text color" },
      group: { ar: "الألوان", en: "Colors" },
      default: cfg.palette.text,
    },
    {
      key: "fontAr",
      type: "font",
      label: { ar: "خط العربية", en: "Arabic font" },
      group: { ar: "الخطوط", en: "Typography" },
      default: cfg.fontAr ?? "Tajawal, system-ui, sans-serif",
    },
    {
      key: "fontEn",
      type: "font",
      label: { ar: "الخط الإنجليزي", en: "English font" },
      group: { ar: "الخطوط", en: "Typography" },
      default: cfg.fontEn ?? "Inter, system-ui, sans-serif",
    },
    {
      key: "cornerStyle",
      type: "select",
      label: { ar: "نمط الحواف", en: "Corner style" },
      group: { ar: "الشكل", en: "Shape" },
      default: cfg.corner ?? "16px",
      options: [
        { value: "0px", label: { ar: "حادة", en: "Sharp" } },
        { value: "8px", label: { ar: "خفيفة", en: "Soft" } },
        { value: "16px", label: { ar: "دائرية", en: "Rounded" } },
        { value: "999px", label: { ar: "كبسولة", en: "Pill" } },
      ],
    },
    {
      key: "showPromoStrip",
      type: "boolean",
      label: { ar: "إظهار شريط العروض", en: "Show promo strip" },
      group: { ar: "العناصر", en: "Elements" },
      default: cfg.showPromoStrip ?? true,
    },
    {
      key: "showNewsletter",
      type: "boolean",
      label: { ar: "إظهار النشرة البريدية", en: "Show newsletter" },
      group: { ar: "العناصر", en: "Elements" },
      default: cfg.showNewsletter ?? true,
    },
    {
      key: "announcementAr",
      type: "text",
      label: { ar: "نص الإعلان (عربي)", en: "Announcement text (Arabic)" },
      group: { ar: "المحتوى", en: "Content" },
      default: cfg.announcement.ar,
    },
    {
      key: "announcementEn",
      type: "text",
      label: { ar: "نص الإعلان (إنجليزي)", en: "Announcement text (English)" },
      group: { ar: "المحتوى", en: "Content" },
      default: cfg.announcement.en,
    },
  ];
}

/**
 * Build a complete, valid theme package from a compact config. Reuses the same
 * vetted layout/template structure as the reference theme so the layout tree
 * only ever references KNOWN_COMPONENTS.
 */
export function makeBuiltinTheme(cfg: ThemeConfig): ThemePackage {
  return {
    manifest: {
      id: cfg.id,
      name: cfg.name,
      description: cfg.description,
      version: cfg.version ?? "1.0.0",
      author: cfg.author ?? "Nexus Cart",
      engineVersion: THEME_ENGINE_VERSION,
      settings: buildSettings(cfg),
    },
    tokens: {
      colors: {
        primary: "$setting:primaryColor",
        accent: "$setting:accentColor",
        background: "$setting:backgroundColor",
        text: "$setting:textColor",
        "header-bg": cfg.palette.headerBg,
        "footer-bg": cfg.palette.footerBg,
        "footer-text": cfg.palette.footerText,
      },
      fonts: {
        ar: "$setting:fontAr",
        en: "$setting:fontEn",
        heading: "$setting:fontEn",
      },
      radii: {
        base: "$setting:cornerStyle",
        card: "$setting:cornerStyle",
        button: "$setting:cornerStyle",
        banner: "$setting:cornerStyle",
      },
      spacing: {
        section: "3rem",
        gutter: "1.25rem",
      },
    },
    layout: {
      header: [
        {
          id: "promo",
          component: "header.promostrip",
          visible: "$setting:showPromoStrip",
          settings: {
            textAr: "$setting:announcementAr",
            textEn: "$setting:announcementEn",
          },
        },
        {
          id: "main-bar",
          component: "header.bar",
          slots: {
            start: [{ id: "logo", component: "header.logo" }],
            center: [{ id: "nav", component: "header.nav" }],
            end: [
              { id: "search", component: "header.search" },
              { id: "actions", component: "header.actions" },
            ],
          },
        },
      ],
      footer: [
        { id: "footer-cols", component: "footer.columns" },
        { id: "footer-newsletter", component: "footer.newsletter", visible: "$setting:showNewsletter" },
        { id: "footer-social", component: "footer.social" },
        { id: "footer-payment", component: "footer.payment" },
        { id: "footer-copy", component: "footer.copyright" },
      ],
      home: [
        { id: "intro", component: "template.slot", template: "intro" },
        { id: "sections", component: "home.sections" },
      ],
      product: [{ id: "product", component: "product.detail" }],
      listing: [{ id: "listing", component: "listing.grid" }],
      cart: [{ id: "cart", component: "cart.view" }],
      customPage: [{ id: "page", component: "page.content" }],
    },
    templates: {
      intro: `<div class="nx-intro">
  {{# if store.name }}<h2 class="nx-intro__title">{{ store.name }}</h2>{{/ if }}
  {{# if store.tagline }}<p class="nx-intro__text">{{ store.tagline }}</p>{{/ if }}
</div>`,
    },
    styles: `
.nx-intro {
  text-align: center;
  padding: var(--nx-space-section, 3rem) var(--nx-space-gutter, 1.25rem);
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--nx-color-primary, #4f46e5) 8%, transparent),
    color-mix(in srgb, var(--nx-color-accent, #14b8a6) 8%, transparent));
  border-radius: var(--nx-radius-banner, 16px);
}
.nx-intro__title {
  font-family: var(--nx-font-heading, inherit);
  color: var(--nx-color-primary, #4f46e5);
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  margin: 0 0 0.5rem;
}
.nx-intro__text {
  color: var(--nx-color-text, #0f172a);
  opacity: 0.8;
  margin: 0;
}
`,
  };
}

// ----- The additional first-party themes (beyond the reference) -----

const MIDNIGHT_LUXE = makeBuiltinTheme({
  id: "midnight-luxe",
  name: { ar: "ميدنايت لوكس", en: "Midnight Luxe" },
  description: {
    ar: "ثيم فاخر بخلفية داكنة ولمسات ذهبية، مثالي للعلامات الراقية والمجوهرات والساعات.",
    en: "A dark, gold-accented luxury theme — perfect for premium brands, jewelry, and watches.",
  },
  palette: {
    primary: "#c9a14a",
    accent: "#e9d7a9",
    background: "#0b0b0f",
    text: "#f5f3ec",
    headerBg: "#0b0b0f",
    footerBg: "#000000",
    footerText: "#cbbf9a",
  },
  fontEn: "Playfair Display, Georgia, serif",
  corner: "0px",
  announcement: {
    ar: "توصيل فاخر مجاني لجميع الطلبات",
    en: "Complimentary luxury delivery on all orders",
  },
});

const CORAL_BOUTIQUE = makeBuiltinTheme({
  id: "coral-boutique",
  name: { ar: "كورال بوتيك", en: "Coral Boutique" },
  description: {
    ar: "ثيم أنثوي عصري بدرجات المرجاني الدافئة، مصمّم لمتاجر الأزياء والإكسسوارات.",
    en: "A warm, modern boutique theme in coral tones — built for fashion and accessories stores.",
  },
  palette: {
    primary: "#ff6b6b",
    accent: "#ffd166",
    background: "#fffaf7",
    text: "#3d2c2c",
    headerBg: "#ffffff",
    footerBg: "#3d2c2c",
    footerText: "#ffe8e0",
  },
  corner: "999px",
  announcement: {
    ar: "تشكيلة الموسم الجديدة وصلت الآن",
    en: "The new season collection has arrived",
  },
});

const TECH_PULSE = makeBuiltinTheme({
  id: "tech-pulse",
  name: { ar: "تك بالس", en: "Tech Pulse" },
  description: {
    ar: "ثيم حيوي عالي التباين بأزرق كهربائي، مناسب للإلكترونيات والأجهزة الذكية.",
    en: "A high-contrast, electric-blue theme tuned for electronics and gadget stores.",
  },
  palette: {
    primary: "#2563eb",
    accent: "#06b6d4",
    background: "#f8fafc",
    text: "#0f172a",
    headerBg: "#0f172a",
    footerBg: "#0f172a",
    footerText: "#cbd5e1",
  },
  corner: "8px",
  announcement: {
    ar: "شحن سريع خلال 24 ساعة",
    en: "Fast 24-hour shipping",
  },
});

const PURE_MINIMAL = makeBuiltinTheme({
  id: "pure-minimal",
  name: { ar: "بيور مينيمال", en: "Pure Minimal" },
  description: {
    ar: "ثيم بسيط نظيف بمساحات واسعة وطباعة أنيقة، يدع منتجاتك تتصدّر المشهد.",
    en: "A clean, minimal theme with generous whitespace and elegant type — lets your products lead.",
  },
  palette: {
    primary: "#111827",
    accent: "#6b7280",
    background: "#ffffff",
    text: "#111827",
    headerBg: "#ffffff",
    footerBg: "#f9fafb",
    footerText: "#6b7280",
  },
  corner: "0px",
  showPromoStrip: false,
  announcement: {
    ar: "إرجاع مجاني خلال 30 يومًا",
    en: "Free returns within 30 days",
  },
});

const VERDE_FRESH = makeBuiltinTheme({
  id: "verde-fresh",
  name: { ar: "فيردي فريش", en: "Verde Fresh" },
  description: {
    ar: "ثيم طبيعي منعش بدرجات الأخضر، مثالي للأطعمة والعضوي ومنتجات العناية الطبيعية.",
    en: "A fresh, natural green theme — ideal for grocery, organic, and natural-care products.",
  },
  palette: {
    primary: "#16a34a",
    accent: "#84cc16",
    background: "#f7fdf4",
    text: "#14352a",
    headerBg: "#ffffff",
    footerBg: "#14352a",
    footerText: "#d6f5d6",
  },
  corner: "16px",
  announcement: {
    ar: "منتجات طازجة كل يوم",
    en: "Fresh products every day",
  },
});

/**
 * All built-in packages, with the reference theme first. The marketplace and
 * the engine route both build their registries from this single source.
 */
export const BUILTIN_THEME_PACKAGES: ThemePackage[] = [
  REFERENCE_THEME,
  MIDNIGHT_LUXE,
  CORAL_BOUTIQUE,
  TECH_PULSE,
  PURE_MINIMAL,
  VERDE_FRESH,
];

/**
 * Marketplace presentation metadata keyed by theme slug. Themes with a
 * non-zero `priceCents` are "paid" — they list a price and can be browsed/
 * previewed, but installing them requires the (separate) paid-themes task, so
 * the marketplace UI gates install on `priceCents === 0` (free/owned).
 */
export const BUILTIN_THEME_CATALOG: ThemeMarketplaceMeta[] = [
  {
    slug: REFERENCE_THEME_ID,
    category: "general",
    rating: 4.9,
    installs: 18420,
    priceCents: 0,
    publishedAt: "2026-01-15",
    swatches: ["#4f46e5", "#14b8a6", "#ffffff", "#0f172a"],
    features: [
      { ar: "تدرّجات هادئة ومساحات واسعة", en: "Calm gradients and airy spacing" },
      { ar: "دعم كامل للعربية والإنجليزية وRTL", en: "Full Arabic/English & RTL support" },
      { ar: "ألوان وخطوط قابلة للتخصيص بالكامل", en: "Fully customizable colors and fonts" },
    ],
  },
  {
    slug: "midnight-luxe",
    category: "luxury",
    rating: 4.8,
    installs: 9230,
    priceCents: 0,
    publishedAt: "2026-03-02",
    swatches: ["#c9a14a", "#e9d7a9", "#0b0b0f", "#f5f3ec"],
    features: [
      { ar: "خلفية داكنة فاخرة مع لمسات ذهبية", en: "Premium dark canvas with gold accents" },
      { ar: "طباعة كلاسيكية أنيقة", en: "Elegant classic typography" },
      { ar: "مثالي للمجوهرات والساعات", en: "Perfect for jewelry and watches" },
    ],
  },
  {
    slug: "coral-boutique",
    category: "fashion",
    rating: 4.7,
    installs: 12750,
    priceCents: 0,
    publishedAt: "2026-04-10",
    swatches: ["#ff6b6b", "#ffd166", "#fffaf7", "#3d2c2c"],
    features: [
      { ar: "حواف دائرية ودرجات مرجانية دافئة", en: "Rounded corners and warm coral tones" },
      { ar: "مصمّم للأزياء والإكسسوارات", en: "Designed for fashion and accessories" },
      { ar: "بطاقات منتجات حيوية", en: "Lively product cards" },
    ],
  },
  {
    slug: "tech-pulse",
    category: "electronics",
    rating: 4.6,
    installs: 8110,
    priceCents: 4900,
    compareAtCents: 9900,
    publishedAt: "2026-02-20",
    swatches: ["#2563eb", "#06b6d4", "#f8fafc", "#0f172a"],
    features: [
      { ar: "تباين عالٍ وأزرق كهربائي", en: "High contrast with electric blue" },
      { ar: "مهيّأ للإلكترونيات والأجهزة", en: "Tuned for electronics and gadgets" },
      { ar: "تصميم سريع وواضح", en: "Fast, legible layout" },
    ],
  },
  {
    slug: "pure-minimal",
    category: "minimal",
    rating: 4.9,
    installs: 15300,
    priceCents: 0,
    publishedAt: "2026-05-05",
    swatches: ["#111827", "#6b7280", "#ffffff", "#111827"],
    features: [
      { ar: "مساحات بيضاء واسعة وطباعة هادئة", en: "Generous whitespace and quiet type" },
      { ar: "يركّز الانتباه على المنتجات", en: "Keeps attention on the products" },
      { ar: "أداء ممتاز وبساطة", en: "Excellent performance and simplicity" },
    ],
  },
  {
    slug: "verde-fresh",
    category: "food",
    rating: 4.5,
    installs: 6040,
    priceCents: 3900,
    compareAtCents: 6900,
    publishedAt: "2026-04-28",
    swatches: ["#16a34a", "#84cc16", "#f7fdf4", "#14352a"],
    features: [
      { ar: "درجات خضراء طبيعية منعشة", en: "Fresh, natural green palette" },
      { ar: "مثالي للأطعمة والمنتجات العضوية", en: "Ideal for grocery and organic goods" },
      { ar: "إحساس نظيف وموثوق", en: "Clean, trustworthy feel" },
    ],
  },
];
